import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Box,
  Chip,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Person,
  Business,
  DirectionsCar,
  CreditCard,
  AttachMoney,
  ChevronLeft,
  Phone,
  Email,
  LocationOn,
  Work,
  CalendarToday,
  Security,
  Speed,
  LocalShipping,
  Assessment,
  People,
  TrendingUp,

} from '@mui/icons-material';
import { quikqAuthService } from '../services/quikqAuth';
import { quikqApiService, UserData, DriverInfo } from '../services/quikqApi';
import { useNavigate } from 'react-router-dom';

const AccountDetails: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userData, setUserData] = useState<UserData | null>(null);
  const [user] = useState(quikqAuthService.getCurrentUser());

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const currentUser = quikqAuthService.getCurrentUser();
        if (!currentUser?.startCode) {
          throw new Error('No start code found');
        }

        // Fetch carrier info using the correct API endpoint
        const carrierInfo = await quikqApiService.getCarrierInfo(currentUser.startCode);
        // console.log('🔍 Carrier info:', carrierInfo);
        
        // Fetch drivers using the correct API endpoint
        const drivers = await quikqApiService.getCarrierDrivers(currentUser.startCode);
        // console.log('🔍 Drivers data:', drivers);
        
        // Fetch cards using the correct API endpoint
        const cards = await quikqApiService.getCarrierCards(currentUser.startCode);
        // console.log('🔍 Cards data:', cards);
        
        // Construct user data from the correct API responses
        const data: UserData = {
          carrier: {
            startCode: carrierInfo.startCode || currentUser.startCode,
            carrierName: carrierInfo.carrierName || 'NorthStar Fuel',
            address: carrierInfo.address || 'N/A',
            city: carrierInfo.city || 'N/A',
            state: carrierInfo.state || 'N/A',
            zip: carrierInfo.zip || 'N/A',
            phone: carrierInfo.phone || 'N/A',
            email: carrierInfo.email || currentUser.userId,
            isActive: carrierInfo.isActive || true,
            remainingCredit: carrierInfo.remainingCredit || 0,
            cards: cards,
          },
          drivers: drivers,
          trailers: [], // TODO: Add trailer API endpoint if available
          qcodes: [], // TODO: Add qcode API endpoint if available
          stats: {
            totalStations: 0,
            activeStations: 0,
            totalTransactions: 0,
            totalRevenue: 0,
            activeDrivers: drivers.filter(d => d.isActive).length,
            activeTrailers: 0,
            activeQCodes: 0,
          },
        };
        
        setUserData(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching user data:', error);
        setError('Unable to load account details. Please try again later.');
        setLoading(false);
        
        // Set basic user data from auth service as fallback
        const currentUser = quikqAuthService.getCurrentUser();
        if (currentUser) {
          setUserData({
            carrier: {
              startCode: currentUser.startCode?.trim() || '',
              carrierName: 'NorthStar Fuel',
              address: '123 Mock Street',
              city: 'Mock City',
              state: 'CA',
              zip: '90210',
              phone: '(555) 123-4567',
              email: currentUser.userId,
              isActive: true,
            },
            drivers: [],
            trailers: [],
            qcodes: [],
            stats: {
              totalStations: 0,
              activeStations: 0,
              totalTransactions: 0,
              totalRevenue: 0,
              activeDrivers: 0,
              activeTrailers: 0,
              activeQCodes: 0,
            },
          });
        }
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  if (!user) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">Please log in to view account details.</Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  // Admin Dashboard
  const renderAdminDashboard = (data: UserData) => (
    <Box>
      <Typography variant="h4" gutterBottom>
        NorthStar Fuel Admin
      </Typography>
      
      {/* Stats Cards */}
      <Box display="flex" flexWrap="wrap" gap={3}>
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Business color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">{data.stats.totalCarriers || 0}</Typography>
                <Typography variant="body2" color="textSecondary">Total Carriers</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <People color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">{data.stats.totalDrivers || 0}</Typography>
                <Typography variant="body2" color="textSecondary">Total Drivers</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <Assessment color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">{data.stats.totalTransactions.toLocaleString()}</Typography>
                <Typography variant="body2" color="textSecondary">Total Transactions</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <TrendingUp color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">${(data.stats.totalRevenue / 1000000).toFixed(1)}M</Typography>
                <Typography variant="body2" color="textSecondary">Total Revenue</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  // Carrier Dashboard
  const renderCarrierDashboard = (data: UserData) => (
    <Box>
      <Typography variant="h4" gutterBottom>
        Carrier Dashboard - Start Code: {data.carrier.startCode}
      </Typography>
      
      {/* Stats Cards */}
      <Box display="flex" flexWrap="wrap" gap={3}>
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <People color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">{data.drivers.length}</Typography>
                <Typography variant="body2" color="textSecondary">Active Drivers</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <LocalShipping color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">{data.trailers.length}</Typography>
                <Typography variant="body2" color="textSecondary">Trailers</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <CreditCard color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">{data.carrier.cards?.length || 0}</Typography>
                <Typography variant="body2" color="textSecondary">Fuel Cards</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
        
        <Card sx={{ flex: '1 1 250px', minWidth: '200px' }}>
          <CardContent>
            <Box display="flex" alignItems="center">
              <AttachMoney color="primary" sx={{ mr: 2 }} />
              <Box>
                <Typography variant="h4">${(data.carrier.remainingCredit || 0).toLocaleString()}</Typography>
                <Typography variant="body2" color="textSecondary">Remaining Credit</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Drivers Section */}
      <Box display="flex" flexWrap="wrap" gap={3} mt={3}>
        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Drivers ({data.drivers.length})
            </Typography>
            <List dense>
              {data.drivers.slice(0, 5).map((driver: DriverInfo) => (
                <ListItem key={driver.driverId}>
                  <ListItemIcon>
                    <Person />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${driver.firstName} ${driver.lastName}`}
                    secondary={`${driver.driverId} - ${driver.status}`}
                  />
                  <Chip 
                    label={driver.status} 
                    color={driver.status === 'Active' ? 'success' : 'default'}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        {/* Trailers Section */}
        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Trailers ({data.trailers.length})
            </Typography>
            <List dense>
              {data.trailers.slice(0, 5).map((trailer: any) => (
                <ListItem key={trailer.trailerId}>
                  <ListItemIcon>
                    <LocalShipping />
                  </ListItemIcon>
                  <ListItemText
                    primary={trailer.trailerId}
                    secondary={`${trailer.trailerType} - ${trailer.status}`}
                  />
                  <Chip 
                    label={trailer.status} 
                    color={trailer.status === 'Active' ? 'success' : 'default'}
                    size="small"
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Cards Section */}
      {data.carrier.cards && data.carrier.cards.length > 0 && (
        <Box mt={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fuel Cards ({data.carrier.cards.length})
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Card Number</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Asset ID</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.carrier.cards.map((card: any) => (
                      <TableRow key={card.cardNo}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            <CreditCard sx={{ mr: 1, color: 'text.secondary' }} />
                            {card.cardNo}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={card.cardType || 'N/A'} 
                            size="small"
                            color={card.cardType === 'Driver' ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell>{card.assetId || 'N/A'}</TableCell>
                        <TableCell>
                          <Chip 
                            label={card.status}
                            size="small"
                            color={card.status === 'Active' ? 'success' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* QCodes Section */}
      <Box mt={3}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active QCodes ({data.qcodes.filter((q: any) => q.qcodeStatus === 'Active').length})
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>QCode</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Driver</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.qcodes
                    .filter((q: any) => q.qcodeStatus === 'Active')
                    .slice(0, 10)
                    .map((qcode: any) => (
                    <TableRow key={qcode.qcode}>
                      <TableCell>{qcode.qcode}</TableCell>
                      <TableCell>${qcode.amount}</TableCell>
                      <TableCell>{qcode.driverId}</TableCell>
                      <TableCell>
                        <Chip 
                          label={qcode.qcodeStatus} 
                          color={qcode.qcodeStatus === 'Active' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{new Date(qcode.qcDate).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  // Driver Dashboard
  const renderDriverDashboard = (data: UserData) => (
    <Box>
      <Typography variant="h4" gutterBottom>
        Driver Profile
      </Typography>
      
      {/* Driver Info Card */}
      <Box display="flex" flexWrap="wrap" gap={3}>
        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                <Person />
              </Avatar>
              <Box>
                <Typography variant="h5">
                  {data.drivers[0]?.firstName || 'N/A'} {data.drivers[0]?.lastName || 'N/A'}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Driver ID: {data.drivers[0]?.driverId || 'N/A'}
                </Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <Work />
                </ListItemIcon>
                <ListItemText
                  primary="Status"
                  secondary={data.drivers[0]?.status || 'N/A'}
                />
                <Chip 
                  label={data.drivers[0]?.status || 'N/A'} 
                                      color={data.drivers[0]?.status === 'Active' ? 'success' : 'default'}
                  size="small"
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CalendarToday />
                </ListItemIcon>
                <ListItemText
                  primary="Date of Birth"
                  secondary={data.drivers[0]?.dob ? new Date(data.drivers[0].dob).toLocaleDateString() : 'N/A'}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <CalendarToday />
                </ListItemIcon>
                <ListItemText
                  primary="Hire Date"
                  secondary={data.drivers[0]?.hireDate ? new Date(data.drivers[0].hireDate).toLocaleDateString() : 'N/A'}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Security />
                </ListItemIcon>
                <ListItemText
                  primary="License"
                  secondary={`${data.drivers[0]?.license || 'N/A'} (${data.drivers[0]?.licenseState || 'N/A'})`}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <DirectionsCar />
                </ListItemIcon>
                <ListItemText
                  primary="Unit Assigned"
                  secondary={data.drivers[0]?.unitAssigned || 'N/A'}
                />
              </ListItem>
              
              <ListItem>
                <ListItemIcon>
                  <Speed />
                </ListItemIcon>
                <ListItemText
                  primary="Driver Type"
                  secondary={data.drivers[0]?.driverType || 'N/A'}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
        
        {/* Contact Info Card */}
        <Card sx={{ flex: '1 1 400px' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contact Information
            </Typography>
            
            <List dense>
              {data.drivers[0]?.emailAddress && (
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText
                    primary="Email"
                    secondary={data.drivers[0]?.emailAddress}
                  />
                </ListItem>
              )}
              
              {data.drivers[0]?.phone && data.drivers[0].phone.length > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <Phone />
                  </ListItemIcon>
                  <ListItemText
                    primary="Phone"
                    secondary={data.drivers[0]?.phone[0].phone}
                  />
                </ListItem>
              )}
              
              {data.drivers[0]?.address && data.drivers[0].address.length > 0 && (
                <ListItem>
                  <ListItemIcon>
                    <LocationOn />
                  </ListItemIcon>
                  <ListItemText
                    primary="Address"
                    secondary={`${data.drivers[0]?.address[0].line1}, ${data.drivers[0]?.address[0].city}, ${data.drivers[0]?.address[0].state} ${data.drivers[0]?.address[0].zip}`}
                  />
                </ListItem>
              )}
              
              <ListItem>
                <ListItemIcon>
                  <Business />
                </ListItemIcon>
                <ListItemText
                  primary="Division"
                  secondary={data.drivers[0]?.division || 'N/A'}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4, pb: { xs: 12, sm: 12, md: 12 } }}>
      {/* Header with Split Watermark - Left: NSFLogo, Right: Empty (for future custom logo) */}
      <Box sx={{ 
        position: 'relative', 
        mb: 3,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -50,
          left: -50,
          width: '100%',
          height: '200px',
          background: `url('/NSFIcon.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          opacity: 0.08,
          zIndex: 0,
          pointerEvents: 'none'
        }
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          position: 'relative',
          zIndex: 1
        }}>
          <Box
            onClick={() => navigate('/profile')}
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              color: 'text.primary',
              mr: 2,
              '&:hover': { opacity: 0.7 }
            }}
          >
            <ChevronLeft sx={{ fontSize: 20, mr: 0.5 }} />
            <Typography variant="body2" fontWeight="500">Back</Typography>
          </Box>
        </Box>

        <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ position: 'relative', zIndex: 1 }}>
          Account Details
        </Typography>
      </Box>

      <Paper sx={{ p: 3 }}>
        {userData && renderAdminDashboard(userData)}
        {userData && renderCarrierDashboard(userData)}
        {userData && renderDriverDashboard(userData)}
        
        {!userData && (
          <Alert severity="info">
            No data available for your account type. Please contact support if you believe this is an error.
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default AccountDetails; 